import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check, X } from "lucide-react";
import api from "@/lib/api";

// Masked password + confirm inputs with a live, color-coded complexity checklist and match indicator.
// Calls onChange(password, isValid) whenever inputs change.
export function PasswordFields({ onChange, label = "New password", autoFocus = false }) {
  const [policy, setPolicy] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    api.get("/settings/password-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy({ min_length: 6 }));
  }, []);

  const checks = policy ? [
    { key: "len", label: `At least ${policy.min_length} characters`, ok: pw.length >= policy.min_length },
    policy.require_uppercase && { key: "up", label: "An uppercase letter", ok: /[A-Z]/.test(pw) },
    policy.require_lowercase && { key: "low", label: "A lowercase letter", ok: /[a-z]/.test(pw) },
    policy.require_number && { key: "num", label: "A number", ok: /[0-9]/.test(pw) },
    policy.require_symbol && { key: "sym", label: "A symbol", ok: /[^A-Za-z0-9]/.test(pw) },
  ].filter(Boolean) : [];

  const allMet = checks.length > 0 && checks.every((c) => c.ok);
  const matches = pw.length > 0 && pw === confirm;
  const valid = allMet && matches;

  useEffect(() => { onChange(pw, valid); /* eslint-disable-next-line */ }, [pw, confirm, policy]);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="pf-pw">{label}</Label>
        <div className="relative mt-1.5">
          <Input id="pf-pw" type={show ? "text" : "password"} value={pw} autoFocus={autoFocus}
            onChange={(e) => setPw(e.target.value)} className="rounded-sm pr-10" data-testid="password-input" />
          <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="password-toggle" aria-label={show ? "Hide password" : "Show password"}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div>
        <Label htmlFor="pf-cf">Confirm password</Label>
        <Input id="pf-cf" type={show ? "text" : "password"} value={confirm}
          onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 rounded-sm" data-testid="password-confirm" />
      </div>

      {checks.length > 0 && (
        <ul className="space-y-1" data-testid="password-checklist">
          {checks.map((c) => (
            <li key={c.key} className={`flex items-center gap-2 text-xs transition-colors ${c.ok ? "text-green-600" : "text-muted-foreground"}`} data-testid={`pwreq-${c.key}-${c.ok ? "met" : "unmet"}`}>
              {c.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5 text-muted-foreground/60" />}
              {c.label}
            </li>
          ))}
        </ul>
      )}

      {confirm.length > 0 && (
        <p className={`flex items-center gap-1.5 text-xs ${matches ? "text-green-600" : "text-destructive"}`} data-testid="password-match">
          {matches ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
          {matches ? "Passwords match" : "Passwords don't match"}
        </p>
      )}
    </div>
  );
}
